// src/components/conduction-social/ProjectVisual.tsx
import { Tempo } from "@/javascript/music-objects";
import { Project, ProjectTrack, ProjectTrackItem } from "@/javascript/project-objects";
import { Box, Group, ScrollArea, Stack, Text, Tooltip, useMantineTheme } from "@mantine/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import MidiBlocks from "@/components/midi-blocks";

type ProjectVisualProps = {
	project: Project;
	/** Height per track row in px (default 52) */
	rowHeight?: number;
	/** Base horizontal pixels per measure (default 24). Zoom multiplies this value. */
	pxPerMeasure?: number;
	/** Show measure numbers at the top (default true) */
	showMeasureNumbers?: boolean;
	/** Width of the fixed label column on the left (default 160) */
	labelColumnWidth?: number;

	/** Audio sync: current audio time (in seconds) */
	currentTimeSec?: number;
	/** Beats per measure to convert tempo→seconds/measure (fallback = 4) */
	beatsPerMeasure?: number;
	/** Draw the moving playhead line (default true) */
	showPlayhead?: boolean;

	/** Continuous drag scrubbing callback (fires on pointer-move while dragging) */
	onScrub?: (seconds: number) => void;
	/** Single-click scrub callback (fires on click/PointerUp if it was a click, not a drag) */
	onScrubClick?: (seconds: number) => void;
};

function itemTrimmedStart(it: ProjectTrackItem): number { return it.getTrimmedStartMeasure(); }
function itemTrimmedLength(it: ProjectTrackItem): number { return it.getTrimmedLength(); }
function binarySearchLE(arr: number[], x: number): number {
	let lo = 0, hi = arr.length - 1, ans = 0;
	while (lo <= hi) {
		const mid = (lo + hi) >> 1;
		if (arr[mid] <= x) { ans = mid; lo = mid + 1; } else hi = mid - 1;
	}
	return ans;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_SENSITIVITY = 0.0015;

const ProjectVisual: React.FC<ProjectVisualProps> = ({
	project,
	rowHeight = 52,
	pxPerMeasure = 24,
	showMeasureNumbers = true,
	labelColumnWidth = 160,

	currentTimeSec,
	beatsPerMeasure = 4,
	showPlayhead = true,

	onScrub,
	onScrubClick,
}) => {
	const theme = useMantineTheme();

	const viewportRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);

	const [hoverItemId, setHoveringItemId] = useState<string | null>(null);
	const [zoom, setZoom] = useState<number>(1);

	// drag state
	const draggingRef = useRef(false);
	const movedRef = useRef(false); // distinguish click vs drag
	const lastSecondsRef = useRef<number | null>(null);

	const ppm = pxPerMeasure * zoom;

	const { tracks, totalMeasures, globalStart } = useMemo(() => {
		let globalStart = Number.POSITIVE_INFINITY;
		const t: {
			track: ProjectTrack;
			label: string;
			items: { it: ProjectTrackItem; start: number; len: number; end: number }[];
		}[] = (project.tracks ?? []).map((trk) => {
			const label = trk.getInstrument()?.name ?? "Track";
			const itemsSorted = [...trk.items].sort((a, b) => itemTrimmedStart(a) - itemTrimmedStart(b));
			const items = itemsSorted.map((it) => {
				const start = Math.max(0, itemTrimmedStart(it));
				const len = Math.max(1, itemTrimmedLength(it));
				const end = start + len;
				return { it, start, len, end };
			});
			if (items.length > 0 && items[0].start < globalStart) globalStart = items[0].start;
			return { track: trk, label, items };
		});

		const maxEnd = t.reduce((mx, trk) => {
			const e = trk.items.reduce((m, it) => Math.max(m, it.end), 0);
			return Math.max(mx, e);
		}, 0);

		const total = Math.max(1, Math.min(maxEnd || 1, Project.maxMeasures || 1));
		return {
			tracks: t,
			totalMeasures: total - (Number.isFinite(globalStart) ? globalStart : 0),
			globalStart: Number.isFinite(globalStart) ? globalStart : 0
		};
	}, [project]);

	// tempo → seconds
	const measureStartTimes = useMemo(() => {
		if (!Number.isFinite(totalMeasures) || totalMeasures <= 0) return [0];
		const starts: number[] = new Array(totalMeasures + 1).fill(0);
		let acc = 0;
		starts[0] = 0;
		for (let m = 0; m < totalMeasures; m++) {
			const tempo: Tempo = project.getLatestTempo(globalStart + m) ?? Tempo.DEFAULT_TEMPO;
			const bpm = tempo.getBpm ? tempo.getBpm() : 120;
			const secPerMeasure = (beatsPerMeasure * 60) / (bpm || 120);
			starts[m] = acc;
			acc += secPerMeasure;
		}
		starts[totalMeasures] = acc;
		return starts;
	}, [project, totalMeasures, beatsPerMeasure, globalStart]);

	// time → measure float
	const playheadMeasure = useMemo(() => {
		if (!showPlayhead || currentTimeSec == null || measureStartTimes.length === 0) return null;
		const t = Math.max(0, currentTimeSec);
		const i = binarySearchLE(measureStartTimes, t);
		const startT = measureStartTimes[i] ?? 0;
		const endT = measureStartTimes[i + 1] ?? startT;
		const span = Math.max(1e-6, endT - startT);
		const frac = Math.min(1, Math.max(0, (t - startT) / span));
		return Math.min(totalMeasures, i + frac);
	}, [currentTimeSec, measureStartTimes, showPlayhead, totalMeasures]);

	const totalWidthPx = Math.ceil(totalMeasures * ppm);

	const colors = [
		theme.colors.red,
		theme.colors.blue,
		theme.colors.yellow
	];

	/** Convert a clientX inside a lane → seconds */
	const laneClientXToSeconds = (laneEl: HTMLDivElement, clientX: number): number => {
		const rect = laneEl.getBoundingClientRect();
		const xWithinLane = clientX - rect.left; // lane excludes label column already
		const measure = Math.max(0, Math.min(totalMeasures, xWithinLane / ppm));
		const i = Math.min(Math.floor(measure), measureStartTimes.length - 2);
		const startT = measureStartTimes[i] ?? 0;
		const endT = measureStartTimes[i + 1] ?? startT;
		const frac = measure - i;
		return startT + (endT - startT) * frac;
	};

	/** Pointer handlers for lanes (shared for ruler and rows) */
	const onLanePointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
		if (!onScrub && !onScrubClick) return;
		const lane = e.currentTarget;
		lane.setPointerCapture(e.pointerId);
		draggingRef.current = true;
		movedRef.current = false;

		const seconds = laneClientXToSeconds(lane, e.clientX);
		lastSecondsRef.current = seconds;
		onScrub?.(seconds);
	};
	const onLanePointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
		if (!draggingRef.current || !onScrub) return;
		const lane = e.currentTarget;
		const seconds = laneClientXToSeconds(lane, e.clientX);
		if (Math.abs((lastSecondsRef.current ?? seconds) - seconds) > 0.0001) {
			movedRef.current = true;
			lastSecondsRef.current = seconds;
			onScrub(seconds);
		}
	};
	const onLanePointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
		if (!(onScrub || onScrubClick)) return;
		const lane = e.currentTarget;
		try { lane.releasePointerCapture(e.pointerId); } catch { }
		const didMove = movedRef.current;
		draggingRef.current = false;
		movedRef.current = false;

		// Single-click: no move during drag → fire onScrubClick once
		if (!didMove && onScrubClick) {
			const seconds = laneClientXToSeconds(lane, e.clientX);
			onScrubClick(seconds);
		}
	};

	/** Zoom (Ctrl/⌘ + wheel) */
	useEffect(() => {
		const vp = viewportRef.current;
		const content = contentRef.current;
		if (!vp || !content) return;

		const onWheel = (e: WheelEvent) => {
			if (!(e.ctrlKey || e.metaKey)) return;
			e.preventDefault();

			const prev = zoom;
			const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev * (1 - e.deltaY * ZOOM_SENSITIVITY)));
			if (next === prev) return;

			const vpRect = vp.getBoundingClientRect();
			const cursorXInViewport = e.clientX - vpRect.left;
			const contentXBefore = vp.scrollLeft + cursorXInViewport;
			const scale = next / prev;
			const contentXAfter = contentXBefore * scale;
			const newScrollLeft = contentXAfter - cursorXInViewport;

			setZoom(next);
			requestAnimationFrame(() => {
				vp.scrollLeft = Math.max(0, newScrollLeft);
			});
		};

		vp.addEventListener("wheel", onWheel, { passive: false });
		return () => vp.removeEventListener("wheel", onWheel as any);
	}, [zoom]);

	return (
		<ScrollArea
			w="100%"
			h="100%"
			viewportRef={(node) => { viewportRef.current = node; }}
		>
			<Box
				ref={contentRef}
				style={{ minWidth: totalWidthPx + labelColumnWidth + 24, boxSizing: "border-box" }}
			>
				<Stack gap={4}>
					{showMeasureNumbers && (
						<Group gap={0} wrap="nowrap" align="center" style={{ width: "100%" }}>
							<Box style={{ width: labelColumnWidth + 8, minWidth: labelColumnWidth }} />
							<Box
								onPointerDown={onLanePointerDown}
								onPointerMove={onLanePointerMove}
								onPointerUp={onLanePointerUp}
								style={{ position: "relative", height: 20, flex: 1, cursor: (onScrub || onScrubClick) ? "pointer" : "default" }}
							>
								{/* Grid */}
								<Box
									style={{
										position: "absolute", inset: 0,
										backgroundImage: "linear-gradient(to right, rgba(0,0,0,0.08) 1px, transparent 1px)",
										backgroundSize: `${ppm}px 100%`,
										pointerEvents: "none",
									}}
								/>
								{/* Measure labels every 4 */}
								<Box style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
									{Array.from({ length: totalMeasures + 1 }).map((_, i) =>
										i % 4 ? null : (
											<Text
												key={`m-${i}`} fz={10} c="dimmed"
												style={{ position: "absolute", left: i * ppm + 2, top: 2, whiteSpace: "nowrap" }}
											>
												{i + globalStart}
											</Text>
										)
									)}
								</Box>
							</Box>
						</Group>
					)}

					{/* Tracks */}
					{tracks.map((trkWrap, idx) => {
						const trackColor = colors[idx % colors.length];
						return (
							<Group key={`trk-${trkWrap.track.id}`} align="center" wrap="nowrap" gap={8} style={{ width: "100%" }}>
								{/* Label */}
								<Box style={{ width: labelColumnWidth, minWidth: labelColumnWidth, padding: "0 8px", boxSizing: "border-box" }}>
									<Text fw={300} fz="sm" lineClamp={2}>{trkWrap.label}</Text>
								</Box>

								{/* Lane (scrub) */}
								<Box
									onPointerDown={onLanePointerDown}
									onPointerMove={onLanePointerMove}
									onPointerUp={onLanePointerUp}
									style={{
										position: "relative",
										height: rowHeight,
										flex: 1,
										borderRadius: 8,
										background: theme.colors.gray[1],
										overflow: "hidden",
										cursor: (onScrub || onScrubClick) ? "pointer" : "default",
									}}
								>
									{/* Grid */}
									<Box
										style={{
											position: "absolute", inset: 0,
											backgroundImage: "linear-gradient(to right, rgba(0,0,0,0.06) 1px, transparent 1px)",
											backgroundSize: `${ppm}px 100%`,
											pointerEvents: "none",
										}}
									/>

									{/* Items */}
									{trkWrap.items.map(({ it, start, len }, i) => {
										const left = (start - globalStart) * ppm;
										const width = Math.max(8, len * ppm - 4);
										const title = it.getTitle();
										return (
											<Tooltip key={`it-${it.id}-${i}`} label={title} openDelay={250} withArrow>
												<Box
													onMouseEnter={() => setHoveringItemId(it.id)}
													onMouseLeave={() => setHoveringItemId(null)}
													style={{
														position: "absolute",
														left, height: rowHeight, width,
														borderRadius: 4,
														background: trackColor[hoverItemId === it.id ? 3 : 2],
														boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
														opacity: it.ghosted ? 0.8 : 1,
														outline: it.ghosted ? `2px dashed ${theme.colors.gray[6]}` : "none",
													}}
												>
													<MidiBlocks key={it.id} section={it.getSection()} trimStart={it.getTrimStart()} trimEnd={it.getTrimEnd()} />
												</Box>
											</Tooltip>
										);
									})}

									{/* Playhead */}
									{showPlayhead && playheadMeasure != null && (
										<Box
											style={{
												position: "absolute",
												left: playheadMeasure * ppm,
												top: 0, bottom: 0, width: 2,
												background: theme.colors.red[6],
												boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
												pointerEvents: "none",
											}}
										/>
									)}
								</Box>
							</Group>
						);
					})}
				</Stack>
			</Box>
		</ScrollArea>
	);
};

export default ProjectVisual;
